import { useEffect, useState, type FormEvent } from 'react';
import {
  Bookmark, Brain, Check, Pin, Plus, Search, ShieldCheck, Trash2, X,
} from 'lucide-react';
import { memoryApi, type MemoryFact } from '../api/memory';

export default function Memory() {
  const [facts, setFacts] = useState<MemoryFact[]>([]);
  const [enabled, setEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [confirmClear, setConfirmClear] = useState(false);

  const showNotice = (message: string) => {
    setNotice(message);
    window.setTimeout(() => setNotice(null), 3200);
  };

  const load = async (query = search) => {
    setLoading(true);
    setError(null);
    try {
      const [loadedFacts, settings] = await Promise.all([
        memoryApi.listFacts(query.trim() ? { search: query.trim() } : undefined),
        memoryApi.getSettings(),
      ]);
      setFacts(loadedFacts);
      setEnabled(settings.enabled);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const add = async (event?: FormEvent) => {
    event?.preventDefault();
    const content = draft.trim();
    if (!content || busy) return;
    setBusy('add');
    setError(null);
    try {
      await memoryApi.addFact(content);
      setDraft('');
      setSearch('');
      await load('');
      showNotice('Факт добавлен в память');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  const remove = async (id: string) => {
    setBusy(id);
    setError(null);
    try {
      await memoryApi.deleteFact(id);
      setFacts((current) => current.filter((fact) => fact.id !== id));
      showNotice('Факт удалён');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  const togglePin = async (fact: MemoryFact) => {
    setBusy(fact.id);
    setError(null);
    try {
      await memoryApi.updateFact(fact.id, { pinned: !fact.pinned });
      setFacts((current) => current.map((item) => item.id === fact.id ? { ...item, pinned: !item.pinned } : item));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  const toggleEnabled = async () => {
    const next = !enabled;
    setEnabled(next);
    setBusy('toggle');
    setError(null);
    try {
      await memoryApi.setEnabled(next);
      showNotice(next ? 'Память включена' : 'Память приостановлена');
    } catch (e) {
      setEnabled(!next);
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  const clearAll = async () => {
    setBusy('clear');
    setError(null);
    try {
      const result = await memoryApi.forgetAll();
      setFacts([]);
      setSearch('');
      setConfirmClear(false);
      showNotice(result.deleted > 0 ? `Удалено фактов: ${result.deleted}` : 'Память уже была пуста');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  const resetSearch = () => {
    setSearch('');
    void load('');
  };

  return (
    <div className="oa-memory-page">
      <header className="oa-section-heading">
        <div>
          <p className="oa-section-kicker"><Brain /> Умная память</p>
          <h2>Факты, которые помогают отвечать точнее</h2>
          <p>Управляйте тем, что ассистент знает о вас. Изменения действуют и в вебе, и в Telegram.</p>
        </div>
        <button className="oa-memory-clear" onClick={() => setConfirmClear(true)} disabled={facts.length === 0 || Boolean(busy)}>
          <Trash2 /> Очистить всю память
        </button>
      </header>

      {error && <div className="oa-memory-error" role="alert"><X />{error}</div>}

      <section className="oa-memory-control" aria-label="Настройка памяти">
        <div className="oa-memory-control-icon"><ShieldCheck /></div>
        <div>
          <strong>Использовать память в ответах</strong>
          <p>{enabled ? 'Ассистент учитывает сохранённые факты в новых диалогах.' : 'Факты сохранены, но временно не используются.'}</p>
        </div>
        <button
          className={`oa-switch${enabled ? ' is-on' : ''}`}
          role="switch"
          aria-checked={enabled}
          aria-label={enabled ? 'Выключить память' : 'Включить память'}
          disabled={busy === 'toggle'}
          onClick={toggleEnabled}
        ><span /></button>
      </section>

      <section className="oa-memory-tools">
        <form className="oa-memory-add" onSubmit={add}>
          <div>
            <label htmlFor="memory-fact">Добавить факт</label>
            <p>Например: «Отвечай мне по-русски и коротко».</p>
          </div>
          <div className="oa-memory-field-row">
            <input
              id="memory-fact"
              placeholder="Что важно запомнить?"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              maxLength={2000}
            />
            <button type="submit" disabled={!draft.trim() || busy === 'add'}>
              {busy === 'add' ? <span className="oa-button-loader" /> : <Plus />}
              Добавить
            </button>
          </div>
        </form>

        <form className="oa-memory-search" onSubmit={(event) => { event.preventDefault(); void load(); }}>
          <Search />
          <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Найти сохранённый факт" aria-label="Поиск по памяти" />
          {search && <button type="button" onClick={resetSearch} aria-label="Сбросить поиск"><X /></button>}
          <button type="submit">Найти</button>
        </form>
      </section>

      <section className="oa-memory-list" aria-busy={loading}>
        <div className="oa-memory-list-head">
          <div><h3>Сохранённые факты</h3><span>{facts.length}</span></div>
          <p>{search ? `Результаты по запросу «${search}»` : 'Закреплённые факты получают дополнительный приоритет.'}</p>
        </div>

        {loading ? (
          <div className="oa-memory-state"><span className="oa-memory-spinner" /><p>Загружаем память…</p></div>
        ) : facts.length === 0 ? (
          <div className="oa-memory-state">
            <div><Bookmark /></div>
            <h3>{search ? 'Ничего не найдено' : 'Память пока пуста'}</h3>
            <p>{search ? 'Измените запрос или сбросьте поиск.' : 'Добавьте первый факт — он будет доступен ассистенту в следующих диалогах.'}</p>
            {search && <button onClick={resetSearch}>Показать все факты</button>}
          </div>
        ) : (
          <div className="oa-memory-grid">
            {facts.map((fact) => (
              <article key={fact.id} className={`oa-memory-fact${fact.pinned ? ' is-pinned' : ''}`}>
                <div className="oa-memory-fact-top">
                  <span>{fact.scope === 'global' ? 'Везде' : fact.scope}</span>
                  {fact.pinned && <em><Pin /> Закреплено</em>}
                </div>
                <p>{fact.content}</p>
                <div className="oa-memory-fact-actions">
                  <button onClick={() => void togglePin(fact)} disabled={busy === fact.id}>
                    <Pin />{fact.pinned ? 'Открепить' : 'Закрепить'}
                  </button>
                  <button className="danger" onClick={() => void remove(fact.id)} disabled={busy === fact.id}>
                    <Trash2 />Удалить
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      {notice && <div className="oa-memory-notice" role="status"><Check />{notice}</div>}

      {confirmClear && (
        <div className="oa-confirm-backdrop" onMouseDown={() => busy !== 'clear' && setConfirmClear(false)}>
          <div className="oa-confirm-dialog" role="alertdialog" aria-modal="true" aria-labelledby="clear-memory-title" onMouseDown={(event) => event.stopPropagation()}>
            <div className="oa-confirm-icon"><Trash2 /></div>
            <h3 id="clear-memory-title">Очистить всю память?</h3>
            <p>Все сохранённые факты будут удалены без возможности восстановления. История диалогов останется на месте.</p>
            <div>
              <button onClick={() => setConfirmClear(false)} disabled={busy === 'clear'}>Отмена</button>
              <button className="danger" onClick={() => void clearAll()} disabled={busy === 'clear'}>
                {busy === 'clear' ? 'Удаляем…' : 'Удалить всё'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
